/**
 * @page PermissionMatrixPage
 * @description Permission matrix overview — roles × pages × actions
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import type { RoleDto } from '@pokerroom/types';
import { SIDEBAR_DEFINITION, ACTION_KEY_GROUPS } from '@pokerroom/types';

export default function PermissionMatrixPage() {
  const { t } = useTranslation(['admin', 'common']);

  const {
    data: roles = [],
    isLoading,
    error,
  } = useQuery<RoleDto[]>({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleDto[]>('/roles'),
  });

  if (isLoading) {
    return <div className="p-6 text-base-content/40">{t('common:loading')}</div>;
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
          {t('common:loadFailed', { defaultValue: 'Failed to load data' })}
        </div>
      </div>
    );
  }

  // Collect all page paths
  const allPages: string[] = [];
  for (const entry of SIDEBAR_DEFINITION) {
    if (entry.type === 'single' && entry.pagePath) {
      allPages.push(entry.pagePath);
    }
    if (entry.children) {
      for (const child of entry.children) {
        allPages.push(child.pagePath);
      }
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-base-content mb-6">
        {t('permissionMatrix', { defaultValue: 'Permission Matrix' })}
      </h1>

      {/* Page Permissions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-base-content mb-3">
          {t('pagePermissions', { defaultValue: 'Page Permissions' })}
        </h2>
        <div className="overflow-x-auto">
          <table className="table-auto border-collapse text-sm w-full">
            <thead>
              <tr>
                <th className="border border-base-300 px-3 py-2 bg-base-200 text-left sticky left-0 z-10 min-w-[200px]">
                  {t('page', { defaultValue: 'Page' })}
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="border border-base-300 px-3 py-2 bg-base-200 text-center min-w-[100px]"
                  >
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allPages.map((pagePath) => (
                <tr key={pagePath} className="hover:bg-base-200/50">
                  <td className="border border-base-300 px-3 py-1.5 font-mono text-xs sticky left-0 bg-base-100 z-10">
                    {pagePath}
                  </td>
                  {roles.map((role) => {
                    const perm = role.pagePermissions.find((p) => p.pagePath === pagePath);
                    const visible = perm?.isVisible ?? false;
                    return (
                      <td key={role.id} className="border border-base-300 px-3 py-1.5 text-center">
                        {visible ? (
                          <span className="text-success font-bold">&#10003;</span>
                        ) : (
                          <span className="text-base-content/20">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Permissions */}
      <div>
        <h2 className="text-lg font-semibold text-base-content mb-3">
          {t('actionPermissions', { defaultValue: 'Action Permissions' })}
        </h2>
        <div className="overflow-x-auto">
          <table className="table-auto border-collapse text-sm w-full">
            <thead>
              <tr>
                <th className="border border-base-300 px-3 py-2 bg-base-200 text-left sticky left-0 z-10 min-w-[200px]">
                  {t('action', { defaultValue: 'Action' })}
                </th>
                {roles.map((role) => (
                  <th
                    key={role.id}
                    className="border border-base-300 px-3 py-2 bg-base-200 text-center min-w-[100px]"
                  >
                    {role.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTION_KEY_GROUPS.map((group) => (
                <React.Fragment key={`group-${group.labelKey}`}>
                  <tr>
                    <td
                      colSpan={roles.length + 1}
                      className="border border-base-300 px-3 py-1.5 bg-base-200 font-semibold text-xs text-base-content/70"
                    >
                      {t(group.labelKey, { defaultValue: group.labelKey })}
                    </td>
                  </tr>
                  {group.actions.map((actionKey) => (
                    <tr key={actionKey} className="hover:bg-base-200/50">
                      <td className="border border-base-300 px-3 py-1.5 font-mono text-xs sticky left-0 bg-base-100 z-10 pl-6">
                        {actionKey}
                      </td>
                      {roles.map((role) => {
                        const perm = role.actionPermissions.find((a) => a.actionKey === actionKey);
                        const allowed = perm?.isAllowed ?? false;
                        return (
                          <td
                            key={role.id}
                            className="border border-base-300 px-3 py-1.5 text-center"
                          >
                            {allowed ? (
                              <span className="text-success font-bold">&#10003;</span>
                            ) : (
                              <span className="text-base-content/20">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
